package com.kaihuoy.home.screens

import androidx.compose.runtime.*
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import com.kaihuoy.shared.data.User
import com.kaihuoy.shared.data.InductionRecord
import com.kaihuoy.shared.data.InductionTemplate
import com.kaihuoy.components.common.*
import com.kaihuoy.components.common.*
import com.kaihuoy.web.router.AppRouterState
import com.kaihuoy.web.FirebaseService
import kotlinx.browser.window
import kotlin.time.Clock
import dev.gitlive.firebase.database.DatabaseReference
import kotlinx.coroutines.flow.first

private const val INDUCTION_TOTAL_STEPS = 5
private const val INDUCTION_FLOW_VERSION = 2
private const val INDUCTION_GUIDE_URL = "https://firebasestorage.googleapis.com/v0/b/kuormakirja-75150.firebasestorage.app/o/templates%2FLiite%2013_MslKy%C3%B6stiKaihuOy_Yleisohjeet%20(1).pdf?alt=media&token=96720e03-d5fb-4b0e-b769-82fa402d0e10"
private const val INDUCTION_GUIDE_VIEWER_IFRAME_URL = "/pdf-viewer.html?v=3#$INDUCTION_GUIDE_URL"

@Composable
fun NewInductionScreen(user: User?, isAdmin: Boolean) {
    val isMobile = window.innerWidth <= 900
    var currentStep by remember { mutableStateOf(1) } // 1 = ohjesivu, 2 = perustiedot, 3 = yleinen perehdytys, 4 = työmaaperehdytys, 5 = yhteenveto

    // Perustiedot
    var employeeName by remember { mutableStateOf(user?.displayName ?: "") }
    var birthDate by remember { mutableStateOf(user?.birthDate ?:"") }
    var employer by remember { mutableStateOf(user?.employer ?:"") }
    var taxNumber by remember { mutableStateOf(user?.taxNumber ?:"") }
    var address by remember { mutableStateOf(user?.address ?:"") }
    var phone by remember { mutableStateOf(user?.phone ?:"") }

    // Yleinen perehdyttäminen (14 kohtaa)
    val generalItems = listOf(
        "1. Yrityksen esittely, liikeidea, organisaatio",
        "2. Toimintapolitiikka ja tavoitteet, Laatujärjestelmä",
        "3. Työajat ja poissaolot",
        "4. Palkka asiat, lomat",
        "5. Työterveys",
        "6. Esimiehen, työtovereiden esittely",
        "7. Käytettävät lomakkeet",
        "8. Työsuojeluasioiden esittely",
        "9. Päihteiden suhteen noudatetaan nollatoleranssia",
        "10. Poikkeamien käsittely",
        "11. Työmenetelmät",
        "12. Koneet (KKH,TRD) ja koneiden käyttö",
        "13. Hallilla työskentely, sähkötyökalut",
        "14. Nostoapuvälineet"
    )

    // Työmaaperehdyttäminen (17 kohtaa liitteestä)
    val workSiteItems = listOf(
        "1. Rakennuskohteen/Työmaan/Urakan esittely",
        "2. Toteutusorganisaatio: Tilaaja, pää-, sivu- ja aliurakoitsijat",
        "3. Työmaan laatu- ja turvallisuussuunnitelmat",
        "4. Henkilöstötilat",
        "5. Työmaan, -paikan järjestys ja siisteys, jätelajittelu",
        "6. Työterveyshuolto ja ensiapuvalmius",
        "7. Paloturvallisuus, sammutuskalusto, tulityöt ja tupakointi",
        "8. Tärkeimmät rakennuskoneet ja käyttösäännöt",
        "9. Rakennusaikaiset sähköasennukset",
        "10. Putoamissuojakset, kulkutiet, telineet, portaat ja niiden kunnossapito",
        "11. Suojakypärän ja muiden suojaimien käyttö, huolto ja säilytys",
        "12. Terveydelle vaaralliset aineet, materiaalien käyttöturvallisuus",
        "13. Työntekijän velvollisuus ilmoittaa havaitut puutteet ja viat esimiehelle",
        "14. Käyttöönotto- ja viikkotarkastukset",
        "15. Työpaikan työsuojeluorganisaatio",
        "16. Työmaakierros"
    )

    var checkedGeneral by remember { mutableStateOf(generalItems.associateWith { false }) }
    var checkedWorkSite by remember { mutableStateOf(workSiteItems.associateWith { false }) }
    var remarksGeneral by remember { mutableStateOf(generalItems.associateWith { "" }) }
    var remarksWorkSite by remember { mutableStateOf(workSiteItems.associateWith { "" }) }

    // Koulutukset/Pätevyydet
    val courses = listOf("Työturvallisuus", "Tulityö", "Tieturva 1", "Ensiapu", "Vesityökortti", "Tieturva 2")
    var trainingValidity by remember { mutableStateOf(courses.associateWith { "" }) }

    var templates by remember { mutableStateOf<List<InductionTemplate>>(emptyList()) }
    var selectedTemplateId by remember { mutableStateOf<String?>(null) }
    var showTemplatePicker by remember { mutableStateOf(false) }

    var draftId by remember { mutableStateOf<String?>(null) }
    var isLoadingDraft by remember { mutableStateOf(true) }
    var draftLoaded by remember { mutableStateOf(false) }

    val scope = rememberCoroutineScope()

    suspend fun saveDraft(status: String = "draft") {
        val uid = user?.uid ?: return
        val ref: DatabaseReference = FirebaseService.database.reference("inductions")

        val record = InductionRecord(
            id = draftId ?: "",
            userId = uid,
            userName = employeeName,
            birthDate = birthDate,
            employer = employer,
            taxNumber = taxNumber,
            address = address,
            phone = phone,
            training = trainingValidity,
            siteName = "", // Tarvittaessa työmaan nimi tähän
            generalChecked = checkedGeneral.filter { it.value }.map { it.key.replace(Regex("[.#$/\\[\\]]"), " ") },
            workSiteChecked = checkedWorkSite.filter { it.value }.map { it.key.replace(Regex("[.#$/\\[\\]]"), " ") },
            generalRemarks = remarksGeneral.filter { it.value.isNotBlank() }.mapKeys { it.key.replace(Regex("[.#$/\\[\\]]"), " ") },
            workSiteRemarks = remarksWorkSite.filter { it.value.isNotBlank() }.mapKeys { it.key.replace(Regex("[.#$/\\[\\]]"), " ") },
            introducerName = user.displayName,
            timestamp = Clock.System.now().toEpochMilliseconds(),
            status = status,
            currentStep = currentStep,
            flowVersion = INDUCTION_FLOW_VERSION
        )

        if (draftId.isNullOrBlank()) {
            val newRef = ref.push()
            draftId = newRef.key
            newRef.setValue(record.copy(id = draftId ?: newRef.key.orEmpty()))
        } else {
            ref.child(draftId!!).setValue(record)
        }
    }

    fun showDraftSavedToast() {
        com.kaihuoy.components.common.AppFeedback.showSuccess("Luonnos tallennettu.")
    }

    fun showSaveErrorToast(e: Exception) {
        com.kaihuoy.components.common.AppFeedback.showError("Tallennus epäonnistui: ${e.message}")
    }

    LaunchedEffect(user?.uid) {
        scope.launch {
            try {
                FirebaseService.database.reference("inductionTemplates").valueEvents.collect { snapshot ->
                    templates = snapshot.children.mapNotNull { it.value<InductionTemplate>() }
                }
            } catch (e: kotlinx.coroutines.CancellationException) {
                // Normaali peruutus
            } catch (e: Exception) {
                console.error("Templates fetch failed", e)
            }
        }

        if (user?.uid == null) {
            isLoadingDraft = false
            return@LaunchedEffect
        }

        try {
            val snapshot = FirebaseService.database.reference("inductions")
                .orderByChild("userId")
                .equalTo(user.uid)
                .valueEvents
                .first()
            val drafts = snapshot.children.mapNotNull { child ->
                val draft = try { child.value<InductionRecord>() } catch (e: Exception) { null }
                if (draft != null && draft.status != "completed") {
                    if (draft.id.isBlank()) draft.copy(id = child.key ?: "") else draft
                } else {
                    null
                }
            }.sortedByDescending { it.timestamp }

            val latest = drafts.firstOrNull()
            if (latest != null) {
                draftId = latest.id
                employeeName = latest.userName
                birthDate = latest.birthDate
                employer = latest.employer
                taxNumber = latest.taxNumber
                address = latest.address
                phone = latest.phone
                trainingValidity = latest.training
                currentStep = migrateInductionStep(latest.currentStep, latest.flowVersion)
                
                val general = latest.generalChecked
                val workSite = latest.workSiteChecked
                    val genRemarks = latest.generalRemarks
                    val siteRemarks = latest.workSiteRemarks

                    checkedGeneral = generalItems.associateWith { it in general }
                    checkedWorkSite = workSiteItems.associateWith { it in workSite }
                    remarksGeneral = generalItems.associateWith { genRemarks[it.replace(Regex("[.#$/\\[\\]]"), " ")] ?: "" }
                    remarksWorkSite = workSiteItems.associateWith { siteRemarks[it.replace(Regex("[.#$/\\[\\]]"), " ")] ?: "" }
                }
        } catch (e: Exception) {
            console.error("Luonnoksen lataus epäonnistui", e)
        } finally {
            isLoadingDraft = false
            draftLoaded = true
        }
    }

    Div(attrs = {
        style {
            if (isMobile) padding(16.px, 8.px) else padding(40.px, 20.px)
            maxWidth(1000.px)
            property("margin", "0 auto")
            backgroundColor(KaihuTheme.Background)
            minHeight(100.vh)
            boxSizing("border-box")
            property("overflow-x", "hidden")
        }
    }) {
        Div(attrs = {
            style {
                backgroundColor(KaihuTheme.Surface)
                if (isMobile) padding(16.px) else padding(32.px)
                borderRadius(16.px)
                property("box-shadow", KaihuTheme.ShadowMd)
                boxSizing("border-box")
                property("overflow-x", "hidden")
            }
        }) {
            H1(attrs = {
                style {
                    marginTop(0.px)
                    marginBottom(8.px)
                    color(KaihuTheme.TextPrimary)
                    fontSize(if (isMobile) 1.4.em else 2.em)
                    property("word-break", "break-word")
                }
            }) { Text("Työhönopastus / Perehdytys") }

            P(attrs = {
                style {
                    color(KaihuTheme.TextSecondary)
                    marginBottom(32.px)
                    fontSize(1.1.em)
                }
            }) {
                Text("Vaihe $currentStep / $INDUCTION_TOTAL_STEPS")
            }

            if (isLoadingDraft) {
                Div(attrs = { style { padding(40.px); textAlign("center") } }) {
                    P { Text("Ladataan aiempaa perehdytystä...") }
                }
                return@Div
            }

            if (draftLoaded && draftId != null) {
                Div(attrs = {
                    style {
                        backgroundColor(rgba(16, 185, 129, 0.1))
                        padding(12.px, 16.px)
                        borderRadius(8.px)
                        marginBottom(24.px)
                        property("border", "1px solid ${KaihuTheme.Success}")
                    }
                }) {
                    P(attrs = { style { color(KaihuTheme.Success); fontWeight("500"); margin(0.px) } }) {
                        Text("Luonnos ladattu. Voit jatkaa siitä mihin jäit.")
                    }
                }
            }

            if (currentStep == 1) {
                H3(attrs = {
                    style {
                        marginBottom(24.px)
                        property("border-bottom", "2px solid ${KaihuTheme.Divider}")
                        paddingBottom(8.px)
                    }
                }) {
                    Text("1. Yleisohjeet ennen perehdytystä")
                }

                Div(attrs = {
                    style {
                        padding(20.px)
                        backgroundColor(rgba(37, 99, 235, 0.05))
                        borderRadius(16.px)
                        marginBottom(20.px)
                        property("border", "1px solid rgba(37, 99, 235, 0.12)")
                    }
                }) {
                    P(attrs = { style { margin(0.px, 0.px, 8.px, 0.px); fontWeight("600"); color(KaihuTheme.TextPrimary) } }) {
                        Text("Lue ensin yleisohjeet")
                    }
                    P(attrs = { style { margin(0.px); color(KaihuTheme.TextSecondary); lineHeight("1.6") } }) {
                        Text("Ensimmäisellä sivulla käydään läpi yleiset ohjeet PDF-muodossa. Jatka vasta sen jälkeen perehdytettävän tietoihin ja muihin vaiheisiin.")
                    }
                }

                if (isMobile) {
                    Div(attrs = {
                        style {
                            backgroundColor(Color.white)
                            borderRadius(18.px)
                            property("border", "1px solid ${KaihuTheme.Divider}")
                            property("box-shadow", KaihuTheme.ShadowSm)
                            marginBottom(20.px)
                            padding(20.px)
                        }
                    }) {
                        P(attrs = { style { margin(0.px, 0.px, 16.px, 0.px); color(KaihuTheme.TextSecondary); lineHeight("1.6") } }) {
                            Text("Ohje avautuu uuteen välilehteen. Palaa tähän lomakkeeseen sulkemalla PDF-välilehti.")
                        }
                        StyledButton(
                            text = "📄 Avaa yleisohjeet PDF",
                            onClick = {
                                window.open(INDUCTION_GUIDE_URL, "_blank")
                                currentStep = 2
                                window.scrollTo(0.0, 0.0)
                            },
                            backgroundColor = KaihuTheme.Primary
                        )
                    }
                } else {
                    Div(attrs = {
                        style {
                            backgroundColor(Color.white)
                            borderRadius(18.px)
                            overflow("hidden")
                            property("border", "1px solid ${KaihuTheme.Divider}")
                            property("box-shadow", KaihuTheme.ShadowSm)
                            marginBottom(20.px)
                        }
                    }) {
                        Iframe(attrs = {
                            attr("src", INDUCTION_GUIDE_VIEWER_IFRAME_URL)
                            attr("title", "Yleisohjeet PDF")
                            style {
                                width(100.percent)
                                property("height", "70vh")
                                minHeight(540.px)
                                border(0.px)
                                display(DisplayStyle.Block)
                            }
                        })
                    }
                }

                Div(attrs = {
                    style {
                        marginTop(32.px)
                        display(DisplayStyle.Flex)
                        gap(16.px)
                        property("border-top", "1px solid ${KaihuTheme.Divider}")
                        paddingTop(24.px)
                        flexWrap(FlexWrap.Wrap)
                    }
                }) {
                    StyledButton(
                        text = "Jatka perustietoihin →",
                        onClick = {
                            currentStep = 2
                            window.scrollTo(0.0, 0.0)
                        }
                    )
                }
            } else if (currentStep == 2) {
                H3(attrs = { 
                    style { 
                        marginBottom(24.px); 
                        property("border-bottom", "2px solid ${KaihuTheme.Divider}"); 
                        paddingBottom(8.px) 
                    } 
                }) {
                    Text("2. Perehdytettävän perustiedot")
                }

                if (templates.isNotEmpty()) {
                    Div(attrs = { style { marginBottom(24.px); padding(20.px); backgroundColor(KaihuTheme.Divider); borderRadius(16.px) } }) {
                        P(attrs = { style { fontWeight("bold"); marginBottom(12.px); color(KaihuTheme.TextPrimary) } }) { Text("Käytä valmista perehdytyspohjaa:") }
                        Div(attrs = { style { display(DisplayStyle.Flex); flexWrap(FlexWrap.Wrap); gap(8.px) } }) {
                            templates.forEach { template ->
                                StyledButton(
                                    template.title,
                                    onClick = {
                                        if (template.category == "general" || template.category == "specialized") {
                                            checkedGeneral = template.items.associateWith { true }
                                            com.kaihuoy.components.common.AppFeedback.showInfo("Yleisen perehdytyksen kohdat ladattu pohjasta!")
                                        } else {
                                            checkedWorkSite = template.items.associateWith { true }
                                            com.kaihuoy.components.common.AppFeedback.showInfo("Työmaaperehdytyksen kohdat ladattu pohjasta!")
                                        }
                                        selectedTemplateId = template.id
                                    },
                                    backgroundColor = if (selectedTemplateId == template.id) KaihuTheme.Primary else KaihuTheme.Secondary
                                )
                            }
                        }
                    }
                }

                Div(attrs = {
                    style {
                        display(DisplayStyle.Grid)
                        property("grid-template-columns", if (isMobile) "1fr" else "repeat(auto-fit, minmax(280px, 1fr))")
                        gap(if (isMobile) 12.px else 20.px)
                    }
                }) {
                    StyledInput(label = "Nimi *", value = employeeName, onValueChange = { employeeName = it })
                    StyledDateInput(label = "Syntymäaika", value = birthDate, onValueChange = { birthDate = it })
                    StyledInput(label = "Työnantaja", value = employer, onValueChange = { employer = it })
                    StyledInput(label = "Veronumero", value = taxNumber, onValueChange = { taxNumber = it })
                    StyledInput(label = "Osoite", value = address, onValueChange = { address = it })
                    StyledInput(label = "Puhelin", value = phone, onValueChange = { phone = it })
                }

                H4(attrs = { style { marginTop(32.px); marginBottom(16.px); color(KaihuTheme.TextPrimary) } }) {
                    Text("Koulutus / Kurssit / Pätevyydet")
                }

                Div(attrs = {
                    style {
                        display(DisplayStyle.Grid)
                        property("grid-template-columns", if (isMobile) "1fr" else "repeat(auto-fit, minmax(220px, 1fr))")
                        gap(if (isMobile) 10.px else 16.px)
                        backgroundColor(KaihuTheme.Surface)
                        if (isMobile) padding(12.px) else padding(20.px)
                        borderRadius(12.px)
                    }
                }) {
                    courses.forEach { course ->
                        StyledMonthYearInput(
                            label = course,
                            value = trainingValidity[course] ?: "",
                            onValueChange = { valo ->
                                trainingValidity = trainingValidity.toMutableMap().apply { this[course] = valo }
                            },
                            placeholder = "KK/VV"
                        )
                    }
                }

                Div(attrs = { 
                    style { 
                        marginTop(40.px);
                        display(DisplayStyle.Flex);
                        flexWrap(FlexWrap.Wrap)
                        gap(12.px);
                        property("border-top", "1px solid ${KaihuTheme.Divider}");
                        paddingTop(24.px)
                    }
                }) {
                    StyledButton(
                        text = "Tallenna luonnos",
                        onClick = {
                            scope.launch {
                                try {
                                    saveDraft("draft")
                                    showDraftSavedToast()
                                } catch (e: Exception) {
                                    showSaveErrorToast(e)
                                }
                            }
                        },
                        backgroundColor = KaihuTheme.Grey
                    )

                    StyledButton(
                        text = "Seuraava →",
                        onClick = {
                            if (employeeName.isBlank()) {
                                com.kaihuoy.components.common.AppFeedback.showWarning("Nimi on pakollinen")
                            } else {
                                currentStep = 3
                                scope.launch {
                                    try {
                                        saveDraft("draft")
                                    } catch (e: Exception) {
                                        console.error("Save draft failed", e)
                                    }
                                }
                                window.scrollTo(0.0, 0.0)
                            }
                        }
                    )
                }
            } else if (currentStep == 3) {
                H3(attrs = { 
                    style { 
                        marginBottom(24.px); 
                        property("border-bottom", "2px solid ${KaihuTheme.Divider}"); 
                        paddingBottom(8.px) 
                    } 
                }) {
                    Text("3. Yleinen perehdyttäminen")
                }
                
                Div(attrs = { style { display(DisplayStyle.Grid); gap(12.px) } }) {
                    generalItems.forEach { item ->
                        Div(attrs = { 
                            style { 
                                padding(8.px, 12.px); 
                                backgroundColor(if (checkedGeneral[item] == true) rgba(37, 99, 235, 0.05) else KaihuTheme.Background); 
                                borderRadius(8.px);
                                property("border", "1px solid ${if (checkedGeneral[item] == true) KaihuTheme.Primary else KaihuTheme.Divider}");
                                property("transition", "all 0.2s ease")
                            } 
                        }) {
                            Div(attrs = {
                                style {
                                    display(DisplayStyle.Flex)
                                    alignItems(AlignItems.Center)
                                    flexWrap(FlexWrap.Wrap)
                                    gap(12.px)
                                }
                            }) {
                                StyledCheckbox(
                                    label = item,
                                    checked = checkedGeneral[item] ?: false,
                                    onCheckedChange = { checked ->
                                        checkedGeneral = checkedGeneral.toMutableMap().apply { this[item] = checked }
                                    }
                                )
                                
                                if (checkedGeneral[item] == true) {
                                    Div(attrs = {
                                        style {
                                            flex(1)
                                            minWidth(if (isMobile) 0.px else 200.px)
                                            width(100.percent)
                                        }
                                    }) {
                                        StyledInput(
                                            label = "",
                                            value = remarksGeneral[item] ?: "",
                                            placeholder = "Huomattavaa (valinnainen)",
                                            onValueChange = { remark ->
                                                remarksGeneral = remarksGeneral.toMutableMap().apply { this[item] = remark }
                                            }
                                        )
                                    }
                                }
                            }
                        }
                    }
                }

                Div(attrs = { 
                    style { 
                        marginTop(40.px);
                        display(DisplayStyle.Flex);
                        flexWrap(FlexWrap.Wrap)
                        gap(12.px);
                        property("border-top", "1px solid ${KaihuTheme.Divider}");
                        paddingTop(24.px)
                    }
                }) {
                    StyledButton(
                        text = "← Edellinen",
                        onClick = {
                            scope.launch { saveDraft("draft") }
                            currentStep = 2
                            window.scrollTo(0.0, 0.0)
                        },
                        backgroundColor = KaihuTheme.Grey
                    )
                    StyledButton(
                        text = "Tallenna luonnos",
                        onClick = {
                            scope.launch {
                                try {
                                    saveDraft("draft")
                                    showDraftSavedToast()
                                } catch (e: Exception) {
                                    showSaveErrorToast(e)
                                }
                            }
                        },
                        backgroundColor = KaihuTheme.Grey
                    )
                    StyledButton(
                        text = "Seuraava →",
                        onClick = {
                            scope.launch { saveDraft("draft") }
                            currentStep = 4
                            window.scrollTo(0.0, 0.0)
                        }
                    )
                }
            } else if (currentStep == 4) {
                H3(attrs = { 
                    style { 
                        marginBottom(24.px); 
                        property("border-bottom", "2px solid ${KaihuTheme.Divider}"); 
                        paddingBottom(8.px) 
                    } 
                }) {
                    Text("4. Työmaaperehdyttäminen")
                }

                Div(attrs = { style { display(DisplayStyle.Grid); gap(12.px) } }) {
                    workSiteItems.forEach { item ->
                        Div(attrs = { 
                            style { 
                                padding(8.px, 12.px); 
                                backgroundColor(if (checkedWorkSite[item] == true) rgba(20, 184, 166, 0.05) else KaihuTheme.Background); 
                                borderRadius(8.px);
                                property("border", "1px solid ${if (checkedWorkSite[item] == true) KaihuTheme.PrimaryTeal else KaihuTheme.Divider}");
                                property("transition", "all 0.2s ease")
                            } 
                        }) {
                            Div(attrs = {
                                style {
                                    display(DisplayStyle.Flex)
                                    alignItems(AlignItems.Center)
                                    flexWrap(FlexWrap.Wrap)
                                    gap(12.px)
                                }
                            }) {
                                StyledCheckbox(
                                    label = item,
                                    checked = checkedWorkSite[item] ?: false,
                                    onCheckedChange = { checked ->
                                        checkedWorkSite = checkedWorkSite.toMutableMap().apply { this[item] = checked }
                                    }
                                )
                                
                                if (checkedWorkSite[item] == true) {
                                    Div(attrs = {
                                        style {
                                            flex(1)
                                            minWidth(if (isMobile) 0.px else 200.px)
                                            width(100.percent)
                                        }
                                    }) {
                                        StyledInput(
                                            label = "",
                                            value = remarksWorkSite[item] ?: "",
                                            placeholder = "Huomattavaa (valinnainen)",
                                            onValueChange = { remark ->
                                                remarksWorkSite = remarksWorkSite.toMutableMap().apply { this[item] = remark }
                                            }
                                        )
                                    }
                                }
                            }
                        }
                    }
                }

                Div(attrs = { 
                    style { 
                        marginTop(40.px);
                        display(DisplayStyle.Flex);
                        flexWrap(FlexWrap.Wrap)
                        gap(12.px);
                        property("border-top", "1px solid ${KaihuTheme.Divider}");
                        paddingTop(24.px)
                    }
                }) {
                    StyledButton(
                        text = "← Edellinen",
                        onClick = {
                            scope.launch { saveDraft("draft") }
                            currentStep = 3
                            window.scrollTo(0.0, 0.0)
                        },
                        backgroundColor = KaihuTheme.Grey
                    )
                    StyledButton(
                        text = "Tallenna luonnos",
                        onClick = {
                            scope.launch {
                                try {
                                    saveDraft("draft")
                                    showDraftSavedToast()
                                } catch (e: Exception) {
                                    showSaveErrorToast(e)
                                }
                            }
                        },
                        backgroundColor = KaihuTheme.Grey
                    )
                    StyledButton(
                        text = "Seuraava →",
                        onClick = {
                            scope.launch { saveDraft("draft") }
                            currentStep = 5
                            window.scrollTo(0.0, 0.0)
                        }
                    )
                }
            } else if (currentStep == 5) {
                H3(attrs = { 
                    style { 
                        marginBottom(24.px); 
                        property("border-bottom", "2px solid ${KaihuTheme.Divider}"); 
                        paddingBottom(8.px) 
                    } 
                }) {
                    Text("5. Yhteenveto")
                }

                Div(attrs = { 
                    style { 
                        padding(24.px); 
                        backgroundColor(KaihuTheme.Background); 
                        borderRadius(12.px); 
                        marginBottom(32.px);
                        property("border", "1px solid ${KaihuTheme.Divider}")
                    } 
                }) {
                    P(attrs = { style { marginBottom(16.px); fontWeight("600") } }) { Text("Nimi: $employeeName") }

                    H4(attrs = { style { marginTop(24.px); color(KaihuTheme.Primary) } }) { Text("Yleinen perehdyttäminen") }
                    Div(attrs = { style { display(DisplayStyle.Flex); flexWrap(FlexWrap.Wrap); gap(8.px) } }) {
                        generalItems.forEach { item ->
                            if (checkedGeneral[item] == true) {
                                Div(attrs = { style { marginBottom(12.px) } }) {
                                    Div(attrs = { style { padding(4.px, 12.px); backgroundColor(rgba(37, 99, 235, 0.1)); borderRadius(16.px); fontSize(0.85.em); color(KaihuTheme.Primary); display(DisplayStyle.InlineBlock) } }) {
                                        Text("✓ $item")
                                    }
                                    if (remarksGeneral[item]?.isNotBlank() == true) {
                                        P(attrs = { style { marginLeft(16.px); fontSize(0.8.em); color(KaihuTheme.TextSecondary); fontStyle("italic"); margin(4.px, 0.px, 0.px, 16.px) } }) {
                                            Text("Huom: ${remarksGeneral[item]}")
                                        }
                                    }
                                }
                            }
                        }
                    }

                    H4(attrs = { style { marginTop(24.px); color(KaihuTheme.PrimaryTeal) } }) { Text("Työmaaperehdyttäminen") }
                    Div(attrs = { style { display(DisplayStyle.Flex); flexWrap(FlexWrap.Wrap); gap(8.px) } }) {
                        workSiteItems.forEach { item ->
                            if (checkedWorkSite[item] == true) {
                                Div(attrs = { style { marginBottom(12.px) } }) {
                                    Div(attrs = { style { padding(4.px, 12.px); backgroundColor(rgba(20, 184, 166, 0.1)); borderRadius(16.px); fontSize(0.85.em); color(KaihuTheme.PrimaryTeal); display(DisplayStyle.InlineBlock) } }) {
                                        Text("✓ $item")
                                    }
                                    if (remarksWorkSite[item]?.isNotBlank() == true) {
                                        P(attrs = { style { marginLeft(16.px); fontSize(0.8.em); color(KaihuTheme.TextSecondary); fontStyle("italic"); margin(4.px, 0.px, 0.px, 16.px) } }) {
                                            Text("Huom: ${remarksWorkSite[item]}")
                                        }
                                    }
                                }
                            }
                        }
                    }
                }

                Div(attrs = { 
                    style { 
                        marginTop(40.px);
                        display(DisplayStyle.Flex);
                        flexWrap(FlexWrap.Wrap)
                        gap(12.px);
                        property("border-top", "1px solid ${KaihuTheme.Divider}");
                        paddingTop(24.px)
                    }
                }) {
                    StyledButton(
                        text = "← Edellinen",
                        onClick = {
                            scope.launch { saveDraft("draft") }
                            currentStep = 4
                            window.scrollTo(0.0, 0.0)
                        },
                        backgroundColor = KaihuTheme.Grey
                    )

                    StyledButton(
                        text = "Tallenna luonnos",
                        onClick = {
                            scope.launch {
                                try {
                                    saveDraft("draft")
                                    showDraftSavedToast()
                                } catch (e: Exception) {
                                    showSaveErrorToast(e)
                                }
                            }
                        },
                        backgroundColor = KaihuTheme.Grey
                    )

                    StyledButton(
                        text = "Valmis ✅",
                        onClick = {
                            if (employeeName.isBlank()) {
                                com.kaihuoy.components.common.AppFeedback.showWarning("Nimi on pakollinen")
                                return@StyledButton
                            }

                            scope.launch {
                                try {
                                    saveDraft("completed")
                                    com.kaihuoy.components.common.AppFeedback.showSuccess("Perehdytys tallennettu onnistuneesti!")
                                    AppRouterState.navigate("hallinta")
                                } catch (e: Exception) {
                                    showSaveErrorToast(e)
                                }
                            }
                        },
                        backgroundColor = KaihuTheme.Success
                    )
                }
            }
        }
    }
}

private fun migrateInductionStep(savedStep: Int, flowVersion: Int): Int {
    if (savedStep <= 0) return 1
    val migratedStep = if (flowVersion >= INDUCTION_FLOW_VERSION) savedStep else savedStep + 1
    return migratedStep.coerceIn(1, INDUCTION_TOTAL_STEPS)
}